import { Link } from 'react-router-dom'
import { SEO } from '@/components/seo'

const yearlyData = [
  { year: '2026', label: '(Start)', clients: 80, aum: '£34M', avgClient: '£425k', revenue: '£255k', hours: 60 },
  { year: '2027', label: '(Year 2)', clients: 95, aum: '£42M', avgClient: '£442k', revenue: '£315k', hours: 45 },
  { year: '2028', label: '(Year 3)', clients: 118, aum: '£56M', avgClient: '£475k', revenue: '£420k', hours: 42 },
  { year: '2029', label: '(Year 4)', clients: 148, aum: '£72M', avgClient: '£486k', revenue: '£540k', hours: 38 },
  { year: '2031', label: '(Exit)', clients: 175, aum: '£88M', avgClient: '£503k', revenue: '£660k', hours: 38, highlight: true },
]

const multipleComparison = [
  { type: 'Basic practice (key-person dependent)', multiple: '2.0 - 2.5x', value: '£1.32M - £1.65M' },
  { type: 'Good practice (some documentation)', multiple: '2.5 - 3.0x', value: '£1.65M - £1.98M' },
  { type: 'Systemised practice (WealthSpace)', multiple: '3.0 - 3.5x', value: '£1.98M - £2.31M', highlight: true },
  { type: 'Premium practice (exceptional metrics)', multiple: '3.5 - 4.5x', value: '£2.31M - £2.97M' },
]

export default function ProjectionMethodologyPage() {
  return (
    <>
      <SEO
        title="Financial Projections Methodology | WealthSpace"
        description="Understand our projection methodology: how we calculate adviser practice growth, exit valuations and the value of financial advice over time."
        url="/projection-methodology"
      />

      <div className="max-w-[800px] mx-auto px-6 py-12">
        <Link
          to="/journey/practice-growth"
          className="inline-flex items-center gap-2 text-sm text-amber-400 hover:gap-3 transition-all mb-8"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
          </svg>
          Back to 80 Clients to Exit
        </Link>

        {/* Header */}
        <div className="flex justify-between items-center pb-4 border-b-[3px] border-amber-500 mb-8">
          <div className="font-serif text-2xl font-semibold text-brand-light">
            Wealth<span className="text-accent-teal">Space</span>
          </div>
          <div className="text-sm text-brand-muted text-right">
            80 Clients to Exit<br />Projection Methodology
          </div>
        </div>

        {/* Title */}
        <div className="text-center mb-10">
          <h1 className="font-serif text-3xl font-medium text-brand-light mb-2">
            The Maths Behind the Projection
          </h1>
          <p className="text-amber-400 font-semibold">Assumptions, calculations, and methodology</p>
        </div>

        {/* Starting Assumptions */}
        <h2 className="font-serif text-xl text-amber-400 font-medium pb-2 border-b-2 border-amber-500/20 mb-6 mt-10">
          Starting Assumptions
        </h2>
        <p className="text-brand-muted mb-6">
          The projection begins with a typical established UK IFA practice - successful but capacity-constrained.
        </p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            { value: '80', label: 'Clients' },
            { value: '£34M', label: 'AUM' },
            { value: '£425k', label: 'Avg per client' },
            { value: '60hrs', label: 'Working week' },
          ].map((stat, i) => (
            <div key={i} className="bg-white border border-slate-200 shadow-card rounded-xl p-4 text-center">
              <div className="font-serif text-2xl text-brand-light mb-1">{stat.value}</div>
              <div className="text-sm text-brand-muted">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Key Assumptions Box */}
        <div className="bg-gradient-to-r from-amber-500/10 to-amber-500/5 border-l-4 border-amber-500 p-5 rounded-r-xl mb-8">
          <h4 className="font-semibold text-amber-600 mb-3">Key Assumptions</h4>
          <ul className="space-y-2 text-amber-700 text-sm">
            <li><strong>Ongoing advice charge:</strong> 0.75% of AUM (illustrative assumption for ongoing service)</li>
            <li><strong>Client retention:</strong> 95%+ annually (illustrative assumption)</li>
            <li><strong>Market growth:</strong> 5% annually (illustrative long-term assumption)</li>
            <li><strong>New client acquisition:</strong> Enabled by time freed through WealthSpace</li>
            <li><strong>Exit multiple:</strong> 3.5x recurring revenue (illustrative; based on observed IFA transaction multiples of 2.5x to 4.5x)</li>
          </ul>
        </div>

        {/* Year-by-Year Projection */}
        <h2 className="font-serif text-xl text-amber-400 font-medium pb-2 border-b-2 border-amber-500/20 mb-6 mt-10">
          Year-by-Year Projection
        </h2>

        <div className="overflow-x-auto mb-8">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-amber-500 text-white">
                <th className="px-4 py-3 text-left font-semibold">Year</th>
                <th className="px-4 py-3 text-right font-semibold">Clients</th>
                <th className="px-4 py-3 text-right font-semibold">AUM</th>
                <th className="px-4 py-3 text-right font-semibold">Avg/Client</th>
                <th className="px-4 py-3 text-right font-semibold">Revenue (0.75%)</th>
                <th className="px-4 py-3 text-right font-semibold">Hours/Week</th>
              </tr>
            </thead>
            <tbody>
              {yearlyData.map((row, i) => (
                <tr
                  key={i}
                  className={row.highlight ? 'bg-amber-50 text-amber-900 font-semibold' : i % 2 === 0 ? 'bg-slate-50' : ''}
                >
                  <td className="px-4 py-3 font-semibold">
                    {row.year} <span className="font-normal text-brand-muted">{row.label}</span>
                  </td>
                  <td className={`px-4 py-3 text-right font-mono ${row.highlight ? 'text-amber-400' : ''}`}>{row.clients}</td>
                  <td className={`px-4 py-3 text-right font-mono ${row.highlight ? 'text-amber-400' : ''}`}>{row.aum}</td>
                  <td className="px-4 py-3 text-right font-mono">{row.avgClient}</td>
                  <td className={`px-4 py-3 text-right font-mono ${row.highlight ? 'text-amber-400' : ''}`}>{row.revenue}</td>
                  <td className="px-4 py-3 text-right font-mono">{row.hours}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Growth Drivers */}
        <h2 className="font-serif text-xl text-amber-400 font-medium pb-2 border-b-2 border-amber-500/20 mb-6 mt-10">
          Growth Drivers
        </h2>

        <div className="grid md:grid-cols-2 gap-8 mb-8">
          <div>
            <h3 className="font-semibold text-brand-light mb-2">Time Reclaimed</h3>
            <p className="text-brand-muted text-sm mb-4">
              WealthSpace automates report writing, meeting prep, and compliance documentation. The average adviser reclaims <strong className="text-brand-light">15+ hours per week</strong> - time reinvested in client acquisition and relationship building.
            </p>

            <h3 className="font-semibold text-brand-light mb-2">Client Growth</h3>
            <p className="text-brand-muted text-sm">
              With capacity freed, advisers can accept referrals previously turned away. The projection assumes <strong className="text-brand-light">15-20 new clients annually</strong> - an illustrative assumption for an adviser with 15 extra hours per week.
            </p>
          </div>
          <div>
            <h3 className="font-semibold text-brand-light mb-2">AUM Growth</h3>
            <p className="text-brand-muted text-sm mb-2">AUM grows through three channels:</p>
            <ul className="text-brand-muted text-sm list-disc ml-5 mb-4">
              <li>New client acquisition</li>
              <li>Existing client contributions</li>
              <li>Market growth (5% assumption)</li>
            </ul>

            <h3 className="font-semibold text-brand-light mb-2">Efficiency Gains</h3>
            <p className="text-brand-muted text-sm">
              Working hours decrease while output increases. By Year 4, the practice handles <strong className="text-brand-light">85% more clients</strong> in <strong className="text-brand-light">37% fewer hours</strong>.
            </p>
          </div>
        </div>

        {/* Exit Valuation */}
        <h2 className="font-serif text-xl text-amber-400 font-medium pb-2 border-b-2 border-amber-500/20 mb-6 mt-10">
          Exit Valuation Methodology
        </h2>

        <div className="bg-slate-900 p-6 rounded-xl text-center mb-6">
          <div className="font-serif text-2xl text-white mb-2">
            £660k revenue × <span className="text-amber-400">3.5x</span> = <span className="text-amber-400">£2.31M</span>
          </div>
          <div className="text-sm text-white/60">Recurring revenue × multiple = enterprise value</div>
        </div>

        <div className="bg-white border border-slate-200 shadow-card p-5 rounded-xl mb-8">
          <p className="text-brand-muted text-sm">
            <strong className="text-brand-light">Why 3.5x?</strong> Based on observed IFA transaction multiples of 2.5x to 4.5x recurring revenue, we use 3.5x as an illustrative assumption. It reflects a practice with: documented processes, clean compliance records, high client retention, systemised operations, and reduced key-person dependency. WealthSpace is designed to support all five.
          </p>
        </div>

        {/* Multiple Comparison */}
        <h3 className="font-semibold text-brand-light mb-4">Multiple Comparison</h3>
        <div className="overflow-x-auto mb-8">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-amber-500 text-white">
                <th className="px-4 py-3 text-left font-semibold">Practice Type</th>
                <th className="px-4 py-3 text-right font-semibold">Typical Multiple</th>
                <th className="px-4 py-3 text-right font-semibold">Exit Value (£660k rev)</th>
              </tr>
            </thead>
            <tbody>
              {multipleComparison.map((row, i) => (
                <tr
                  key={i}
                  className={row.highlight ? 'bg-amber-500/10' : i % 2 === 0 ? 'bg-slate-50' : ''}
                >
                  <td className={`px-4 py-3 ${row.highlight ? 'font-semibold text-brand-light' : 'text-brand-muted'}`}>{row.type}</td>
                  <td className={`px-4 py-3 text-right font-mono ${row.highlight ? 'font-semibold text-brand-light' : 'text-brand-muted'}`}>{row.multiple}</td>
                  <td className={`px-4 py-3 text-right font-mono ${row.highlight ? 'font-semibold text-brand-light' : 'text-brand-muted'}`}>{row.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Timeline */}
        <h2 className="font-serif text-xl text-amber-400 font-medium pb-2 border-b-2 border-amber-500/20 mb-6 mt-10">
          Timeline Flexibility
        </h2>
        <p className="text-brand-muted mb-6">
          The 5-year projection is illustrative. Actual timelines vary based on starting position, market conditions, and growth ambitions.
        </p>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
          {[
            { value: '3 yrs', label: 'Aggressive growth' },
            { value: '5 yrs', label: 'Moderate (illustrated)', highlight: true },
            { value: '7 yrs', label: 'Conservative pace' },
          ].map((item, i) => (
            <div
              key={i}
              className={`p-4 rounded-xl text-center border-2 ${
                item.highlight
                  ? 'border-amber-500 bg-amber-500/10'
                  : 'border-slate-200 bg-slate-50'
              }`}
            >
              <div className="font-serif text-2xl text-brand-light mb-1">{item.value}</div>
              <div className="text-xs text-brand-muted">{item.label}</div>
            </div>
          ))}
        </div>

        <div className="bg-white border border-slate-200 shadow-card p-5 rounded-xl mb-8">
          <p className="text-brand-muted text-sm">
            <strong className="text-brand-light">The maths works either way.</strong> Whether you're targeting a 3-year sprint or a 7-year marathon, the fundamental equation remains: time freed = capacity for growth = increased enterprise value.
          </p>
        </div>

        {/* What's Not Included */}
        <h2 className="font-serif text-xl text-amber-400 font-medium pb-2 border-b-2 border-amber-500/20 mb-6 mt-10">
          What This Projection Doesn't Include
        </h2>

        <div className="bg-gradient-to-r from-slate-500/10 to-slate-500/5 border-l-4 border-slate-500 p-5 rounded-r-xl mb-8">
          <h4 className="font-semibold text-slate-400 mb-3">Conservative Elements</h4>
          <ul className="space-y-2 text-slate-300 text-sm">
            <li>No initial advice fees (only ongoing revenue modelled)</li>
            <li>No platform rebates or introducer fees</li>
            <li>No income from protection or mortgage referrals</li>
            <li>No value attributed to client portal or engagement tools</li>
            <li>No adjustment for inflation in fees</li>
          </ul>
        </div>

        {/* Footer */}
        <div className="text-center pt-8 border-t border-slate-200">
          <p className="text-brand-light font-semibold mb-2">WealthSpace - Built by Advisers. For Advisers.</p>
          <p className="text-xs text-brand-subtle">
            This projection is illustrative only and does not constitute financial advice. Actual results will vary based on individual circumstances, market conditions, and business decisions.
          </p>
        </div>
      </div>
    </>
  )
}
